/* eslint-disable react/prop-types */
/*
Author: Gilang Romadhanu Tartila
*/

import React, { useState } from 'react';
import SelectBox from '@components/form/SelectBox';

import useApi from '@/hooks/useApi';
import { MsOrder } from '@api/ms-order';
import PropTypes from 'prop-types';

const propTypes = {
  label: PropTypes.string,
  editable: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
  isRequired: PropTypes.bool,
  multiple: PropTypes.bool,
  hasError: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  onChange: PropTypes.func,
  withReset: PropTypes.bool,
  loading: PropTypes.bool,
  customFilter: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  promoType: PropTypes.string,
  onReset: PropTypes.func,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.array,
    PropTypes.object,
  ]),
  formLabelKey: PropTypes.string,
};

const defaultProps = {
  label: 'Promotion',
  editable: true,
  isRequired: false,
  multiple: false,
  hasError: false,
  onChange: () => {},
  withReset: false,
  onReset: () => {},
  loading: false,
  customFilter: {},
  promoType: null,
  value: '',
  formLabelKey: '',
};

const limit = 10;

const PromotionRulesSearch = (props) => {
  const {
    label = 'Promotion',
    editable = true,
    isRequired = false,
    multiple = false,
    hasError = false,
    customFilter = {},
    promoType = null,
    loading: isLoadingProps = false,
    withReset = false,
    onReset = () => {},
    value = '',
    formLabelKey = '',
  } = props;

  const [loading, setLoading] = useState({
    initial: false,
    loadMore: false,
  });
  const [options, setOptions] = useState([]);
  const [skip, setSkip] = useState(0);

  const getPromotionRules = useApi(MsOrder.getPromotionRules);

  const normalizeItems = (items) =>
    items.map((item) => {
      if (item._id) {
        item.id = item._id;
      }
      if (!item.name && item.description) {
        item.name = item.description;
      }
      return item;
    });

  const onSearch = async (val, newSkip = 0) => {
    if (newSkip) {
      setLoading({ ...loading, loadMore: true });
    } else {
      setLoading({ ...loading, initial: true });
    }
    try {
      let response;
      if (promoType === 'cash_before_delivery') {
        const filter = {};
        if (val) {
          filter.name = { $regex: val, $options: 'i' };
        }
        response = await MsOrder.getCashBeforeDelivery({
          params: {
            ...(Object.keys(filter).length ? { filter } : {}),
            skip: newSkip,
            limit,
          },
        });
        if (response?.status == 200) {
          const raw = response?.data?.data;
          const items = Array.isArray(raw) ? raw : raw ? [raw] : [];
          const result = normalizeItems(items);
          if (newSkip) {
            setOptions(options.concat(result));
          } else {
            setOptions(result);
          }
        } else if (!newSkip) {
          setOptions([]);
        }
      } else {
        let filter = {
          is_active: true,
          current_status: 'approved',
          from_date: { $lte: new Date().toISOString() },
          to_date: { $gte: new Date().toISOString() },
        };
        if (customFilter && Object.keys(customFilter).length) {
          filter = {
            ...filter,
            ...customFilter,
          };
        }
        if (val) {
          filter.name = { $regex: val, $options: 'i' };
        }
        if (!val && filter.name) {
          delete filter.name;
        }
        response = await getPromotionRules.request({
          params: {
            filter: filter,
            skip: newSkip,
            limit: limit,
          },
        });
        if (response.status == 200 && response?.data?.data?.length) {
          const result = normalizeItems(response.data.data);
          if (newSkip) {
            setOptions(options.concat(result));
          } else {
            setOptions(result);
          }
        } else {
          if (!newSkip) {
            setOptions([]);
          }
        }
      }

      if (newSkip) {
        setLoading({ ...loading, loadMore: false });
      } else {
        setLoading({ ...loading, initial: false });
      }
    } catch (error) {
      if (newSkip) {
        setLoading({ ...loading, loadMore: false });
      } else {
        setLoading({ ...loading, initial: false });
      }
    }
  };

  return (
    <SelectBox
      label={label}
      options={options}
      value={value}
      placeholder={
        loading.initial || loading.loadMore ? 'Loading...' : `Select ${label}`
      }
      trackBy="id"
      labelKey="name"
      formLabelKey={formLabelKey ? formLabelKey : ''}
      isRequired={isRequired}
      multiple={multiple}
      editable={editable && !loading.initial}
      hasError={hasError}
      withReset={withReset}
      loading={loading.initial}
      loadingCallback={loading.loadMore}
      withCallback={true}
      withLoadMore={true}
      loadMoreEmpty={skip >= options.length}
      onReset={onReset}
      searchCallback={(val) => {
        setSkip(0);
        onSearch(val);
      }}
      onLoadMore={(val) => {
        const newSkip = skip + 10;
        setSkip(newSkip);
        onSearch(val, newSkip);
      }}
      onChange={(val) => {
        props.onChange(val);
      }}
      onReady={() => {
        if (!isLoadingProps) {
          setSkip(0);
          onSearch();
        }
      }}
    />
  );
};

PromotionRulesSearch.propTypes = propTypes;
PromotionRulesSearch.defaultProps = defaultProps;

export default PromotionRulesSearch;
